/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const title = "Android SDK", description = "Hightouch's Android SDK for collecting events", lastUpdatedDate = new Date(1696377600000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: "Android SDK",
        description: "Hightouch's Android SDK for collecting events",
        lastUpdatedDate: "Wed Oct 04 2023 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: null,
        type: null,
        icon: null,
        toc: [{
          "value": "Installation",
          "depth": 0,
          "id": "installation"
        }, {
          "value": "Initialization",
          "depth": 0,
          "id": "initialization"
        }, {
          "value": "Context",
          "depth": 1,
          "id": "context"
        }, {
          "value": "API",
          "depth": 0,
          "id": "api"
        }, {
          "value": "Identify",
          "depth": 1,
          "id": "identify"
        }, {
          "value": "Track",
          "depth": 1,
          "id": "track"
        }, {
          "value": "Screen",
          "depth": 1,
          "id": "screen"
        }, {
          "value": "Group",
          "depth": 1,
          "id": "group"
        }, {
          "value": "Flush",
          "depth": 1,
          "id": "flush"
        }, {
          "value": "Reset",
          "depth": 1,
          "id": "reset"
        }, {
          "value": "Opt out",
          "depth": 1,
          "id": "opt-out"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      p: "p",
      h2: "h2",
      a: "a",
      code: "code",
      pre: "pre",
      span: "span",
      strong: "strong",
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      h3: "h3"
    }, _provideComponents(), props.components);
    return _jsxs(_Fragment, {
      children: [_jsx(_components.p, {
        children: "The Android SDK makes it easy to track events from Java and Kotlin Android applications."
      }), "\n", _jsx(_components.h2, {
        id: "installation",
        children: "Installation"
      }), "\n", _jsxs(_components.p, {
        children: ["The Android SDK is hosted on ", _jsx(_components.a, {
          href: "https://jitpack.io/#ht-sdks/events-sdk-android/",
          children: "Jitpack"
        }), "."]
      }), "\n", _jsxs(_components.p, {
        children: ["You may install it via Gradle by adding it to your ", _jsx(_components.code, {
          children: "settings.gradle"
        }), " and ", _jsx(_components.code, {
          children: "build.gradle"
        }), "."]
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-rust",
          children: [_jsx(_components.span, {
            className: "hljs-comment",
            children: "// settings.gradle"
          }), "\n\nallprojects {\n  repositories {\n    maven { url ", _jsx(_components.span, {
            className: "hljs-symbol",
            children: "'https"
          }), ":", _jsx(_components.span, {
            className: "hljs-comment",
            children: "//jitpack.io' }"
          }), "\n  }\n}\n"]
        })
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-arduino",
          children: [_jsx(_components.span, {
            className: "hljs-comment",
            children: "// build.gradle"
          }), "\n\ndependencies {\n  implementation ", _jsx(_components.span, {
            className: "hljs-string",
            children: "'com.github.ht-sdks.events-sdk-android:analytics:0.+'"
          }), "\n}\n"]
        })
      }), "\n", _jsx(_components.h2, {
        id: "initialization",
        children: "Initialization"
      }), "\n", _jsxs(_components.p, {
        children: ["You should initialize the SDK in a central location and store it in a variable\nfor use across your application. For example, you may want to initialize it in\nyour application's ", _jsx(_components.code, {
          children: "onCreate"
        }), " callback. After initialization, you can use the\nSDK anywhere you import the ", _jsx(_components.code, {
          children: "Analytics"
        }), " class via the singleton instance."]
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Kotlin example"
        }), ":"]
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-scss",
          children: ["import com", _jsx(_components.span, {
            className: "hljs-selector-class",
            children: ".hightouch"
          }), _jsx(_components.span, {
            className: "hljs-selector-class",
            children: ".analytics"
          }), _jsx(_components.span, {
            className: "hljs-selector-class",
            children: ".Analytics"
          }), "\n\nval analytics = Analytics", _jsx(_components.span, {
            className: "hljs-selector-class",
            children: ".Builder"
          }), "(context, \"YOUR_WRITE_KEY\")\n    ", _jsx(_components.span, {
            className: "hljs-selector-class",
            children: ".defaultApiHost"
          }), "(\"us-east-", _jsx(_components.span, {
            className: "hljs-number",
            children: "1"
          }), ".hightouch-events.com/v1\")\n    ", _jsx(_components.span, {
            className: "hljs-selector-class",
            children: ".trackApplicationLifecycleEvents"
          }), "()\n    ", _jsx(_components.span, {
            className: "hljs-selector-class",
            children: ".build"
          }), "()\n\nAnalytics", _jsx(_components.span, {
            className: "hljs-selector-class",
            children: ".setSingletonInstance"
          }), "(analytics)\n"]
        })
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Java example"
        }), ":"]
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-ini",
          children: ["import com.hightouch.analytics.Analytics\n\nAnalytics ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "analytics"
          }), " = new Analytics.Builder(context, ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"YOUR_WRITE_KEY\""
          }), ")\n    .defaultApiHost(\"us-east-1.hightouch-events.com/v1\")\n    .trackApplicationLifecycleEvents()\n    .build()", _jsx(_components.span, {
            className: "hljs-comment",
            children: ";"
          }), "\n\nAnalytics.setSingletonInstance(analytics)", _jsx(_components.span, {
            className: "hljs-comment",
            children: ";"
          }), "\n"]
        })
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Configuration options"
        }), ":"]
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Option"
            }), _jsx(_components.th, {
              children: "Type"
            }), _jsx(_components.th, {
              children: "Description"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "defaultApiHost"
              })
            }), _jsx(_components.td, {
              children: "String"
            }), _jsx(_components.td, {
              children: "The API host to send the tracked events to."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "flushQueueSize"
              })
            }), _jsx(_components.td, {
              children: "int"
            }), _jsx(_components.td, {
              children: "The number of events to queue before sending events to the Hightouch API. Defaults to 20."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "flushInterval"
              })
            }), _jsx(_components.td, {
              children: "int"
            }), _jsx(_components.td, {
              children: "The maximum amount of time (in seconds) to store events locally before forwarding to Hightouch. Defaults to 30 seconds."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "trackApplicationLifecycleEvents"
              })
            }), _jsx(_components.td, {
              children: "n/a"
            }), _jsx(_components.td, {
              children: "Automatically track application lifecycle events (like opening the application)."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "recordScreenViews"
              })
            }), _jsx(_components.td, {
              children: "n/a"
            }), _jsx(_components.td, {
              children: "Automatically track screen views."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "trackDeepLinks"
              })
            }), _jsx(_components.td, {
              children: "n/a"
            }), _jsx(_components.td, {
              children: "Automatically track deep links."
            })]
          })]
        })]
      }), "\n", _jsx(_components.h3, {
        id: "context",
        children: "Context"
      }), "\n", _jsx(_components.p, {
        children: "By default, Highotuch automatically collects context such as the device\noperating system. This context is forwarded with each event."
      }), "\n", _jsxs(_components.p, {
        children: ["To modify the context sent with each event, modify the context dictionary\nreturned by ", _jsx(_components.code, {
          children: "getAnalyticsContext()"
        }), ". You may add additional values, as well as\nremove default metadata."]
      }), "\n", _jsx(_components.p, {
        children: "You may also override the context in individual event calls. See the per-event\nmethods for more information."
      }), "\n", _jsx(_components.h2, {
        id: "api",
        children: "API"
      }), "\n", _jsx(_components.h3, {
        id: "identify",
        children: "Identify"
      }), "\n", _jsxs(_components.p, {
        children: ["The ", _jsx(_components.code, {
          children: "identify"
        }), " method sends an ", _jsx(_components.code, {
          children: "identify"
        }), " event. It accepts user id and trait\ninformation for the current user -- if you only have partial information, you\ncan just supply a single parameter."]
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Java method signature"
        }), ":"]
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-csharp",
          children: ["Analytics.", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "with"
          }), "(context).identify(", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"userId\""
          }), ", ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "new"
          }), " Traits().putEmail(", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"email\""
          }), "), ", _jsx(_components.span, {
            className: "hljs-literal",
            children: "null"
          }), ");\n"]
        })
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Kotlin method signature"
        }), ":"]
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-csharp",
          children: ["Analytics.", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "with"
          }), "(context).identify(", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"userId\""
          }), ", Traits().putEmail(", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"email\""
          }), "), ", _jsx(_components.span, {
            className: "hljs-literal",
            children: "null"
          }), ");\n"]
        })
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Method parameters"
        }), ":"]
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Parameter"
            }), _jsx(_components.th, {
              children: "Type"
            }), _jsx(_components.th, {
              children: "Description"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "userId"
              })
            }), _jsx(_components.td, {
              children: "String"
            }), _jsx(_components.td, {
              children: "The user's persistent ID"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "traits"
              })
            }), _jsx(_components.td, {
              children: "Traits (optional)"
            }), _jsxs(_components.td, {
              children: ["Additional traits about the user, such as ", _jsx(_components.code, {
                children: "email"
              }), " and ", _jsx(_components.code, {
                children: "name"
              }), "."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "options"
              })
            }), _jsx(_components.td, {
              children: "Options (optional)"
            }), _jsxs(_components.td, {
              children: ["Overrides to the event's context may be passed through the ", _jsx(_components.code, {
                children: "options"
              }), " object. Note that this overrides the default context."]
            })]
          })]
        })]
      }), "\n", _jsx(_components.h3, {
        id: "track",
        children: "Track"
      }), "\n", _jsxs(_components.p, {
        children: ["The ", _jsx(_components.code, {
          children: "track"
        }), " method sends a ", _jsx(_components.code, {
          children: "track"
        }), " event. The event name is required, and\nadditional properties that describe the event may be supplied."]
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Java method signature"
        }), ":"]
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-csharp",
          children: ["Analytics.", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "with"
          }), "(context).track(", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"eventName\""
          }), ", ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "new"
          }), " Properties().putValue(", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"custom\""
          }), ", ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"property\""
          }), "), ", _jsx(_components.span, {
            className: "hljs-literal",
            children: "null"
          }), ");\n"]
        })
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Kotlin method signature"
        }), ":"]
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-csharp",
          children: ["Analytics.", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "with"
          }), "(context).track(", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"eventName\""
          }), ", Properties().putValue(", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"custom\""
          }), ", ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"property\""
          }), "), ", _jsx(_components.span, {
            className: "hljs-literal",
            children: "null"
          }), ");\n"]
        })
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Method parameters"
        }), ":"]
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Parameter"
            }), _jsx(_components.th, {
              children: "Type"
            }), _jsx(_components.th, {
              children: "Description"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "name"
              })
            }), _jsx(_components.td, {
              children: "String"
            }), _jsx(_components.td, {
              children: "The name of the event."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "properties"
              })
            }), _jsx(_components.td, {
              children: "Properties (optional)"
            }), _jsxs(_components.td, {
              children: ["Additional properties about the event, such as ", _jsx(_components.code, {
                children: "product_id"
              }), "."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "options"
              })
            }), _jsx(_components.td, {
              children: "Options (optional)"
            }), _jsxs(_components.td, {
              children: ["Overrides to the event's context may be passed through the ", _jsx(_components.code, {
                children: "options"
              }), " object. Note that this overrides the default context."]
            })]
          })]
        })]
      }), "\n", _jsx(_components.h3, {
        id: "screen",
        children: "Screen"
      }), "\n", _jsxs(_components.p, {
        children: ["The ", _jsx(_components.code, {
          children: "screen"
        }), " method sends a ", _jsx(_components.code, {
          children: "screen"
        }), " event. The screen title is required, and\nadditional information about the screen's category and properties may be\nsupplied."]
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Java method signature"
        }), ":"]
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-csharp",
          children: ["Analytics.", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "with"
          }), "(context).screen(", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"category\""
          }), ", ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"name\""
          }), ", ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "new"
          }), " Properties().putValue(", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"custom\""
          }), ", ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"property\""
          }), "), ", _jsx(_components.span, {
            className: "hljs-literal",
            children: "null"
          }), ");\n"]
        })
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Kotlin method signature"
        }), ":"]
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-csharp",
          children: ["Analytics.", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "with"
          }), "(context).screen(", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"category\""
          }), ", ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"name\""
          }), ", Properties().putValue(", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"custom\""
          }), ", ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"property\""
          }), "), ", _jsx(_components.span, {
            className: "hljs-literal",
            children: "null"
          }), ");\n"]
        })
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Method parameters"
        }), ":"]
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Parameter"
            }), _jsx(_components.th, {
              children: "Type"
            }), _jsx(_components.th, {
              children: "Description"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "category"
              })
            }), _jsx(_components.td, {
              children: "String (optional if name is supplied)"
            }), _jsx(_components.td, {
              children: "The screen's category. For example \"Docs\""
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "title"
              })
            }), _jsx(_components.td, {
              children: "String (optional if category is supplied)"
            }), _jsx(_components.td, {
              children: "The screen's name. For example \"Getting started\""
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "properties"
              })
            }), _jsx(_components.td, {
              children: "Properties (optional)"
            }), _jsxs(_components.td, {
              children: ["Additional properties about the event, such as ", _jsx(_components.code, {
                children: "from_link"
              }), "."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "options"
              })
            }), _jsx(_components.td, {
              children: "Options (optional)"
            }), _jsxs(_components.td, {
              children: ["Overrides to the event's context may be passed through the ", _jsx(_components.code, {
                children: "options"
              }), " object. Note that this overrides the default context."]
            })]
          })]
        })]
      }), "\n", _jsx(_components.h3, {
        id: "group",
        children: "Group"
      }), "\n", _jsxs(_components.p, {
        children: ["The ", _jsx(_components.code, {
          children: "group"
        }), " method sends a ", _jsx(_components.code, {
          children: "group"
        }), " event."]
      }), "\n", _jsx(_components.p, {
        children: "The id identifying the user's group is required. Additional traits on the group\ncan be provided."
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Java method signature"
        }), ":"]
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-csharp",
          children: ["Analytics.", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "with"
          }), "(context).", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "group"
          }), "(", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"groupId\""
          }), ", ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "new"
          }), " Traits().putValue(", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"custom\""
          }), ", ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"property\""
          }), "), ", _jsx(_components.span, {
            className: "hljs-literal",
            children: "null"
          }), ");\n"]
        })
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Kotlin method signature"
        }), ":"]
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-csharp",
          children: ["Analytics.", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "with"
          }), "(context).", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "group"
          }), "(", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"groupId\""
          }), ", Traits().putValue(", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"custom\""
          }), ", ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"property\""
          }), "), ", _jsx(_components.span, {
            className: "hljs-literal",
            children: "null"
          }), ");\n"]
        })
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Method parameters"
        }), ":"]
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Parameter"
            }), _jsx(_components.th, {
              children: "Type"
            }), _jsx(_components.th, {
              children: "Description"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "groupId"
              })
            }), _jsx(_components.td, {
              children: "String"
            }), _jsx(_components.td, {
              children: "The id for the group."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "traits"
              })
            }), _jsx(_components.td, {
              children: "Traits (optional)"
            }), _jsxs(_components.td, {
              children: ["Additional traits about the group, such as ", _jsx(_components.code, {
                children: "company_name"
              }), "."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "options"
              })
            }), _jsx(_components.td, {
              children: "Options (optional)"
            }), _jsxs(_components.td, {
              children: ["Overrides to the event's context may be passed through the ", _jsx(_components.code, {
                children: "options"
              }), " object. Note that this overrides the default context."]
            })]
          })]
        })]
      }), "\n", _jsx(_components.h3, {
        id: "flush",
        children: "Flush"
      }), "\n", _jsx(_components.p, {
        children: "The Android SDK buffers events locally before sending them to Hightouch's servers.\nThis minimizes the number of requests made by the SDK and makes the tracking\nnon-blocking."
      }), "\n", _jsxs(_components.p, {
        children: ["To force the local buffer to be sent to Hightouch immediately call the ", _jsx(_components.code, {
          children: "flush"
        }), "\nmethod."]
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Method signature"
        }), ":"]
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-scss",
          children: ["Analytics", _jsx(_components.span, {
            className: "hljs-selector-class",
            children: ".with"
          }), "(context)", _jsx(_components.span, {
            className: "hljs-selector-class",
            children: ".flush"
          }), "()\n"]
        })
      }), "\n", _jsx(_components.h3, {
        id: "reset",
        children: "Reset"
      }), "\n", _jsxs(_components.p, {
        children: ["The ", _jsx(_components.code, {
          children: "reset"
        }), " method resets the ", _jsx(_components.code, {
          children: "identify"
        }), " and ", _jsx(_components.code, {
          children: "group"
        }), " for the local session."]
      }), "\n", _jsxs(_components.p, {
        children: ["The ", _jsx(_components.code, {
          children: "reset"
        }), " method should be called when users log out. This way, if the user\nlogs back in with another account, the userIds and traits from the different\nsessions remain separate."]
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Method signature"
        }), ":"]
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-scss",
          children: ["Analytics", _jsx(_components.span, {
            className: "hljs-selector-class",
            children: ".with"
          }), "(context)", _jsx(_components.span, {
            className: "hljs-selector-class",
            children: ".reset"
          }), "()\n"]
        })
      }), "\n", _jsx(_components.h3, {
        id: "opt-out",
        children: "Opt out"
      }), "\n", _jsxs(_components.p, {
        children: ["The ", _jsx(_components.code, {
          children: "optOut"
        }), " method disabled event tracking. When called, events are not sent\nto Hightouch's servers."]
      }), "\n", _jsxs(_components.p, {
        children: ["Note that the opt out persists across device reboots. To opt back in to\ntracking, pass ", _jsx(_components.code, {
          children: "false"
        }), " to ", _jsx(_components.code, {
          children: "optOut"
        }), "."]
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Method signature"
        }), ":"]
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-csharp",
          children: ["Analytics.", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "with"
          }), "(context).optOut(", _jsx(_components.span, {
            className: "hljs-literal",
            children: "true"
          }), ")\n"]
        })
      })]
    });
  }
}
export default MDXContent;
